import { type BrandingConfig, branding } from '@fsd/shared';
import { Badge, Button, WhatsAppIcon } from '@fsd/ui';
import { Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';

export function Contact() {
  const { t } = useTranslation('home');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const handleWhatsAppClick = () => {
    window.open(branding.contact.whatsapp, '_blank', 'noopener,noreferrer');
  };

  const contactMethods = [
    {
      icon: Mail,
      label: t('contact.other.email.label'),
      value: branding.contact.email,
      href: `mailto:${branding.contact.email}`,
      breakWord: true,
    },
    {
      icon: Phone,
      label: t('contact.other.phone.label'),
      value: branding.contact.phone,
      href: `tel:${branding.contact.phone}`,
      breakWord: false,
    },
  ];

  // Get branches for address display with safety check
  const branches = branding.contact.branches || [];

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="contact"
      className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-32 dark:bg-slate-900"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#1e293b08_1px,transparent_1px),linear-gradient(-45deg,#64748b08_1px,transparent_1px)] bg-size-[40px_40px]"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-primary/10 blur-3xl animate-pulse sm:top-32 sm:right-32"></div>
        <div className="absolute -bottom-16 left-0 h-80 w-80 rounded-full bg-primary/5 blur-3xl animate-pulse delay-1000 sm:bottom-32 sm:left-32"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="mb-12 text-center lg:mb-16"
        >
          <Badge
            variant="outline"
            size="lg"
            icon={<MessageCircle className="h-4 w-4" />}
            className={`mb-6 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            {t('contact.badge')}
          </Badge>

          <h2
            className={`mb-4 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-5xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            {t('contact.title')}
          </h2>
          <p
            className={`mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '200ms' }}
          >
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
          {/* WhatsApp - Main CTA */}
          <div
            className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="relative h-full rounded-2xl border-2 border-[#25D366]/20 bg-linear-to-br from-[#25D366]/10 via-[#25D366]/5 to-transparent p-8 shadow-xl transition-all duration-500 hover:border-[#25D366]/40 hover:shadow-2xl dark:border-[#25D366]/30 dark:from-[#25D366]/20 dark:via-[#25D366]/10">
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 h-24 w-24 rounded-full bg-[#25D366]/20 blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 h-32 w-32 rounded-full bg-[#25D366]/10 blur-2xl"></div>

              <div className="relative z-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] shadow-lg">
                    <WhatsAppIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                      {t('contact.whatsapp.title')}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {t('contact.whatsapp.description')}
                    </p>
                  </div>
                </div>

                <p className="mb-6 text-slate-700 dark:text-slate-300">
                  {t('contact.whatsapp.cta')}
                </p>

                <Button
                  size="lg"
                  onClick={handleWhatsAppClick}
                  className="group w-full bg-[#25D366] text-white shadow-lg transition-all hover:bg-[#20BA5A] hover:shadow-xl"
                >
                  <WhatsAppIcon className="mr-2 h-5 w-5" />
                  {t('contact.whatsapp.button')}
                </Button>
              </div>
            </div>
          </div>

          {/* Other Contact Methods */}
          <div
            className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '400ms' }}
          >
            <h3 className="mb-6 text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t('contact.other.title')}
            </h3>

            {/* Contact Info Cards */}
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <div
                    key={method.label}
                    className={`group rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:border-primary/50 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 fade-in-ready ${isSectionInView ? 'in-view' : ''}`}
                    style={{ transitionDelay: `${500 + index * 100}ms` }}
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                        {method.label}
                      </span>
                    </div>
                    {method.href ? (
                      <a
                        href={method.href}
                        className={`block text-sm text-slate-600 transition-colors hover:text-primary dark:text-slate-400 dark:hover:text-primary ${method.breakWord ? 'break-all' : ''}`}
                      >
                        {method.value.split('\n').map((line: string, i: number, arr: string[]) => (
                          <span key={`${method.label}-line-${i}`}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </span>
                        ))}
                      </a>
                    ) : (
                      <p
                        className={`text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line ${method.breakWord ? 'break-all' : ''}`}
                      >
                        {method.value}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Addresses - Compact Display */}
            {branches.length > 0 && (
              <div
                className={`mt-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 fade-in-ready ${isSectionInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '800ms' }}
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {t('contact.other.address.label')}
                  </span>
                </div>

                <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
                  {branches.map((branch: BrandingConfig['contact']['branches'][number]) => (
                    <div key={branch.name} className="space-y-1">
                      <p className="font-semibold text-slate-900 dark:text-slate-100 text-base">
                        {branch.name} - {branch.address.state}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                        {branch.address.line1}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-500">
                        {branch.address.line2}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
